import { inject, Inject, Injectable } from '@angular/core';
import { Inspection, InspectionResolutionStatus } from './inspection.dto';
import { DOCUMENTS_COLLECTION, ID_FIELD_OPTIONS, MAX_QUERY_RESULTS, NO_LIMIT_SEARCH_PARAMS, Query, SearchResult } from '@damofire/data-commons';
import { COMMONS_ENV, HttpService, ObservableService } from '@damofire/data-angular-commons';
import { collectionData, collectionGroup, DocumentData, Firestore, query, QueryConstraint, where } from '@angular/fire/firestore';
import { Document } from '@damofire/data-storage';
import { Observable } from 'rxjs';
import { FirebaseApp } from '@angular/fire/app';
import { deleteObject, ref, Storage } from '@angular/fire/storage';
import { IssueService } from '../issue/issue.service';
import { AssetService } from '../asset/asset.service';
import { logger } from '@sentry/angular';

@Injectable()
export abstract class InspectionService extends ObservableService<Inspection> {
  protected readonly firestore = inject(Firestore);
  protected readonly storage = inject(Storage);
  protected readonly app = inject(FirebaseApp);

  protected constructor(
    protected readonly http: HttpService,
    protected readonly issueService: IssueService,
    @Inject(COMMONS_ENV) protected readonly environment: any,
    protected readonly assetService: AssetService,
  ) {
    super();
  }

  protected abstract dbCreate(inspection: Partial<Inspection>): Promise<Inspection>;
  abstract update(division: string, id: string, update: DocumentData | Inspection): Promise<void>;
  abstract delete(division: string, id: string): Promise<void>;
  abstract deleteBatch(division: string, inspectionIds: string[], deleteIssues: boolean): Promise<void>;
  abstract search(division: string, sQuery: Query): Promise<SearchResult<Inspection>>;
  abstract get(division: string, id: string): Promise<Inspection>;

  async deleteAll(division: string, inspectionIds: string[], deleteIssues = false): Promise<void> {
    for (const id of inspectionIds) {
      await this.delete(division, id);
      if (deleteIssues) {
        await this.deleteAllInspectionIssues({ _id: id, division: { _id: division } });
      } else {
        await this.removedInspectionFromIssues({ _id: id, division: { _id: division } });
      }
    }
  }

  async create(inspection: Partial<Inspection>): Promise<Inspection> {
    inspection._id = new Date().getTime().toString();
    logger.info('Creating inspection', { category: 'inspection', id: inspection._id, division: inspection.division?._id });
    const divisionId = inspection.division._id;
    if (!inspection.reviews) {
      const assets = (await this.assetService.search(inspection.division._id, { filters: { 'division._id': divisionId }, ...NO_LIMIT_SEARCH_PARAMS })).result;
      inspection.reviews = assets.map((asset) => ({ _id: asset._id, toReview: true }));
    }

    inspection.status = {
      progress: 0,
      status: InspectionResolutionStatus.OPEN,
      reviewedCnt: inspection.reviews.length,
      unreviewedCnt: 0,
      total: inspection.reviews.length,
    };

    return this.dbCreate(inspection);
  }

  async getCsv(query: Query): Promise<string> {
    return this.http.getByQueryCsv(`ww-inspections`, query, null);
  }

  async markAsCompleted(inspection: Inspection): Promise<void> {
    logger.info('Marking inspection as completed', { category: 'inspection', id: inspection._id });
    inspection.reviews.forEach((review) => (review.toReview = false));
    await this.updateReviewsWithStats(inspection);
  }

  listDocuments(inspection: string, isAdmin = false): Observable<Document[]> {
    const q: QueryConstraint[] = [where('inspection._id', '==', inspection)];
    if (!isAdmin) {
      q.push(where('undisclosed', '==', false));
    }
    return collectionData<Document>(query(collectionGroup(this.firestore, DOCUMENTS_COLLECTION), ...q), ID_FIELD_OPTIONS);
  }

  async deleteDocument(document: Document): Promise<void> {
    await deleteObject(ref(this.storage, document.path));
  }

  async updateReviewsWithStats(inspection: Inspection): Promise<void> {
    let reviewedCnt = 0;
    let unreviewedCnt = 0;
    inspection.reviews.forEach((review) => {
      if (review.toReview) {
        unreviewedCnt++;
      } else {
        reviewedCnt++;
      }
    });

    const dbInspection = await this.get(inspection.division._id, inspection._id);
    dbInspection.reviews = inspection.reviews;
    dbInspection.status = {
      progress: Math.round((reviewedCnt / (reviewedCnt + unreviewedCnt)) * 100) || 0,
      status: unreviewedCnt === 0 && reviewedCnt > 0 ? InspectionResolutionStatus.RESOLVED : InspectionResolutionStatus.OPEN,
      reviewedCnt,
      unreviewedCnt,
      total: reviewedCnt + unreviewedCnt,
    };

    await this.update(inspection.division?._id, inspection._id, dbInspection);
  }

  private async deleteAllInspectionIssues(inspection: Inspection): Promise<void> {
    const division = inspection.division._id;
    const issues = await this.issueService.search(division, { filters: { 'inspection._id': +inspection._id }, size: MAX_QUERY_RESULTS });
    logger.info('Deleting inspection issues', { category: 'inspection', division, id: inspection._id, count: issues.result?.length });
    await this.issueService.delete(division, issues.result);
  }

  private async removedInspectionFromIssues(inspection: Inspection): Promise<void> {
    const division = inspection.division._id;
    const issues = await this.issueService.search(division, { filters: { 'inspection._id': +inspection._id }, size: MAX_QUERY_RESULTS });
    logger.info('Removing inspection from issues', { category: 'inspection', division, id: inspection._id, count: issues.result?.length });
    await this.issueService.updateAll(division, issues.result, { inspection: null });
  }
}
